import { BoardScanLine, priceBoardMatchups, SimulationRun } from '../services/api';

/** The matchup simulation takes at most this many drivers per query. */
export const MAX_MATCHUP_DRIVERS = 10;

/** Our chance of each matchup side finishing ahead, by `${driver}|${opponent}` (both directions are filled in). */
export interface MatchupProbs {
  base: Record<string, number>;
  /** Track Specialists, when the run has it. */
  layered?: Record<string, number>;
}

export const matchupKey = (driver: string, opponent: string) => `${driver}|${opponent}`;

/** The board's matchups as unordered pairs (each once), from lines both reads agreed on. */
export const boardMatchups = (lines: BoardScanLine[]): [string, string][] => {
  const seen = new Set<string>();
  const out: [string, string][] = [];
  for (const l of lines) {
    if (l.market !== 'h2h' || l.status !== 'ok' || !l.driver || !l.opponent || l.driver === l.opponent) continue;
    const pair: [string, string] = l.driver < l.opponent ? [l.driver, l.opponent] : [l.opponent, l.driver];
    const k = pair.join('|');
    if (!seen.has(k)) {
      seen.add(k);
      out.push(pair);
    }
  }
  return out;
};

/**
 * Groups of at most `max` drivers such that both drivers of every matchup land in one group: each query prices every
 * pair inside its group, so a board with more drivers than one query takes just needs a few queries.
 */
export const matchupBatches = (pairs: [string, string][], max = MAX_MATCHUP_DRIVERS): string[][] => {
  const groups: Set<string>[] = [];
  for (const [a, b] of pairs) {
    // the group that already has both, else the one that needs fewest new drivers and still fits
    let best: Set<string> | undefined;
    let bestAdd = 3;
    for (const g of groups) {
      const add = (g.has(a) ? 0 : 1) + (g.has(b) ? 0 : 1);
      if (g.size + add <= max && add < bestAdd) {
        best = g;
        bestAdd = add;
      }
    }
    if (!best) {
      best = new Set();
      groups.push(best);
    }
    best.add(a);
    best.add(b);
  }
  return groups.map((g) => Array.from(g));
};

/**
 * Prices the board's matchups from the run on screen (same track, sims, removed tracks and chaos; both models when the
 * run has Track Specialists), all queries at once. Throws if any query fails, so a board is never half priced.
 */
export const priceMatchups = async (lines: BoardScanLine[], run: SimulationRun): Promise<MatchupProbs | null> => {
  const pairs = boardMatchups(lines);
  if (pairs.length === 0) return null;
  const batches = matchupBatches(pairs);
  const models = run.layered ? (['base', 'layered'] as const) : (['base'] as const);
  const results = await Promise.all(
    models.flatMap((model) =>
      batches.map((drivers) =>
        priceBoardMatchups(run.site, run.nSims, drivers, run.excludedTracks, model, run.chaos).then((r) => ({
          model,
          r,
        })),
      ),
    ),
  );
  const out: MatchupProbs = { base: {}, ...(run.layered && { layered: {} }) };
  for (const { model, r } of results) {
    const into = model === 'base' ? out.base : out.layered!;
    for (const h of r.headToHead) {
      into[matchupKey(h.driverA, h.driverB)] = h.probAAhead;
      into[matchupKey(h.driverB, h.driverA)] = 1 - h.probAAhead;
    }
  }
  return out;
};
